import { useMutation } from '@apollo/client';
import { Security } from '../../../../3-pattern/securitiesSelectionTable/securitiesSelectionTable';
import { ModelPortfolioData } from '../createModelPortfolio/createModelPortfolio';
import { TargetAllocation } from '../../../../../interfaces/financialProduct';
import { CREATE_MODEL_PORTFOLIO, TRANSITION_MODEL_PORTFOLIO, UPDATE_MODEL_PORTFOLIO } from '../queries';
import { useGlobalToast } from '../../../../../providers/globalToastProvider';
import { AssetClassTargetAllocation } from '../addModelPortfoliosIPSAndAssetClasses/addModelPortfoliosIPSAndAssetClasses.visual';

export interface FormattedChild {
  financialProductId: string;
  percentage: number;
  isCashEquivalent: boolean;
}

export interface FormattedTargetAllocation {
  secondaryAssetClassId: string;
  min: number;
  max: number;
}

export const formatChildren = (securities: Security[]): FormattedChild[] => {
  if (!securities?.length) return [];

  return securities
    .filter((security) => !security.financialProduct.isCash)
    .map((security) => ({
      financialProductId: security.financialProduct.id?.startsWith('temp-') ? '' : (security.financialProduct.id ?? ''),
      percentage: security.percentage,
      isCashEquivalent: security.isCashEquivalent ?? false,
    }));
};

export const formatTargetAllocations = (targetAllocations: TargetAllocation[]): FormattedTargetAllocation[] => targetAllocations.map((targetAllocation) => ({
  secondaryAssetClassId: targetAllocation.secondaryAssetClass.id ?? '',
  min: targetAllocation.min,
  max: targetAllocation.max,
}));

const updateModelPortfolioState = (
  modelPortfolioDetails: ModelPortfolioData,
  modelPortfolioId: string,
  securities?: Security[],
  targetAllocations?: AssetClassTargetAllocation[],
  zeroPercentCash?: boolean,
): ModelPortfolioData => ({
  ...modelPortfolioDetails,
  id: modelPortfolioId,
  ...(securities?.length ? { securities } : {}),
  ...(targetAllocations?.length ? { targetAllocations } : {}),
  ...(zeroPercentCash ? { zeroPercentCash } : {}),
});

const createUpdateInput = (
  modelPortfolioId: string,
  cleanedInput: Record<string, unknown>,
  securities?: Security[],
  targetAllocations?: AssetClassTargetAllocation[],
  zeroPercentCash?: boolean,
): Record<string, unknown> => ({
  modelPortfolioId,
  ...cleanedInput,
  children: securities?.length ? formatChildren(securities) : [],
  targetAllocations: targetAllocations?.length ? formatTargetAllocations(targetAllocations ?? []) : [],
  zeroPercentCash,
});

const cleanModelPortfolioDetails = (data: Record<string, unknown>): Record<string, unknown> => {
  const cleanedData = { ...data };

  const translatedFields = [
    'riskProfileName',
    'translatedName',
    'translatedPortfolioDescription',
    'translatedInvestmentObjective',
    'translatedPortfolioDisclaimer',
  ];

  const numberFields = [
    'forecastedRateOfReturn',
    'forecastedStandardDeviation',
    'suggestedMinScore',
    'suggestedMaxScore',
    'lowThresholdScore',
    'riskReturnLevel',
  ];

  translatedFields.forEach((field) => {
    if (cleanedData[field]
      && (!(cleanedData[field] as { en?: string; fr?: string }).en?.trim()
      && !(cleanedData[field] as { en?: string; fr?: string }).fr?.trim())) {
      delete cleanedData[field];
    }
  });

  numberFields.forEach((field) => {
    if (cleanedData[field] === null
        || cleanedData[field] === undefined
        || Number.isNaN(cleanedData[field])
    ) {
      delete cleanedData[field];
    }
  });

  return cleanedData;
};

export const useSaveModelPortfolio = () => {
  const { showToast } = useGlobalToast();
  const [createModelPortfolio, { loading: createLoading }] = useMutation(CREATE_MODEL_PORTFOLIO);
  const [updateModelPortfolio, { loading: updateLoading }] = useMutation(UPDATE_MODEL_PORTFOLIO);
  const [transitionModelPortfolio, { loading: transitionLoading }] = useMutation(TRANSITION_MODEL_PORTFOLIO);

  const saveModelPortfolio = async (
    modelPortfolioDetails: ModelPortfolioData,
    transitionToActive?: boolean,
  ) => {
    let modelPortfolioId = modelPortfolioDetails?.id ?? '';

    try {
      const {
        id,
        organizationId,
        creationType,
        targetAllocations,
        portfolioTemplate,
        zeroPercentCash,
        securities,
        originalValues,
        groupBy,
        ...baseModelPortfolioDetails
      } = modelPortfolioDetails;

      const { ...cleanedInput } = cleanModelPortfolioDetails(baseModelPortfolioDetails as Record<string, unknown>);
      let result;

      if (!modelPortfolioId) {
        result = await createModelPortfolio({
          variables: { input: { ...cleanedInput, organizationId } },
        });

        if (!result?.data?.createModelPortfolio?.modelPortfolio?.id) {
          throw new Error('Failed to create model portfolio - no ID returned from create mutation');
        }

        modelPortfolioId = result.data.createModelPortfolio.modelPortfolio.id;
      }

      result = await updateModelPortfolio({
        variables: {
          input: createUpdateInput(modelPortfolioId, cleanedInput, securities, targetAllocations, zeroPercentCash),
        },
        onError: (error) => ({
          ...modelPortfolioDetails,
          id: modelPortfolioId,
        }),
      });

      if (!result?.data?.updateModelPortfolio?.modelPortfolio) {
        throw new Error('Failed to update model portfolio - no data returned from update mutation');
      }

      const updatedModelPortfolio = updateModelPortfolioState(
        modelPortfolioDetails,
        modelPortfolioId,
        securities,
        targetAllocations,
        zeroPercentCash,
      );

      let transitionResult;
      if (transitionToActive && modelPortfolioId) {
        transitionResult = await transitionModelPortfolio({
          variables: {
            input: {
              modelPortfolioId,
              transition: 'activate',
            },
          },
        });

        if (!transitionResult?.data?.transitionModelPortfolio?.modelPortfolio) {
          throw new Error('Failed to transition model portfolio - no data returned from transition mutation');
        }
      }

      showToast({
        message: transitionResult ? 'Active Model Portfolio created successfully.' : 'Model portfolio draft saved successfully.',
        severity: 'success',
      });

      return updatedModelPortfolio;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save model portfolio';
      showToast({
        message: errorMessage,
        severity: 'error',
      });
      throw error;
    }
  };

  return { saveModelPortfolio, loading: createLoading || updateLoading || transitionLoading };
};
